import { FilterService } from "@primevue/core/api";

/**
 * Match mode of the created-date column filters; use it as the filter's `matchMode`. Importing it
 * registers {@link matchesDateRange} with PrimeVue (below), so no table depends on another having
 * been mounted first — an unregistered mode throws in DataTable's local filtering.
 */
export const DATE_RANGE_MATCH_MODE = "dateRange";

/** Local midnight of the given moment, as epoch milliseconds. */
const localDay = (value: string | Date): number =>
  new Date(value).setHours(0, 0, 0, 0);

/**
 * Whether a timestamp falls within the days picked in a `DatePicker selectionMode="range"` filter.
 *
 * Days are compared in the browser's local timezone — the same calendar day `formatDateTime` shows
 * in the column — so a row created at 00:30 local time belongs to that day even when its UTC date
 * is the previous one. An empty filter matches every row; a range with only its start picked
 * matches that single day.
 */
export function matchesDateRange(
  value: string | null | undefined,
  filter: (Date | null)[] | null | undefined
): boolean {
  const [from, to] = filter ?? [];
  if (!from) return true;
  if (!value) return false;

  const day = localDay(value);
  return day >= localDay(from) && day <= localDay(to ?? from);
}

FilterService.register(DATE_RANGE_MATCH_MODE, matchesDateRange);

/**
 * Match mode of the forward deliveries' deadline-month filter; use it as the filter's `matchMode`.
 * Importing it registers {@link matchesDeadlineMonth} with PrimeVue (below).
 */
export const DEADLINE_MONTH_MATCH_MODE = "deadlineMonthIn";

/**
 * The calendar month a forward delivery must be transferred by, as `YYYY-MM`. The deadline is the
 * delivery window's end, so the back office can group open deliveries by the month they fall due.
 */
export function deadlineMonth(windowEnd: string): string {
  return windowEnd.slice(0, 7);
}

/**
 * Whether a delivery falls due in one of the months picked in a `MultiSelect` filter of
 * {@link deadlineMonth} values. No selection matches every row; a row without a delivery never
 * matches a selection.
 */
export function matchesDeadlineMonth(
  windowEnd: string | null | undefined,
  months: string[] | null | undefined
): boolean {
  if (!months?.length) return true;
  if (!windowEnd) return false;
  return months.includes(deadlineMonth(windowEnd));
}

FilterService.register(DEADLINE_MONTH_MATCH_MODE, matchesDeadlineMonth);
